import { PropsWithChildren, useState, useMemo, useCallback } from "react"

import { Box, Stack, Typography } from "@mui/material"
import type { FC, ResponsiveImageType } from ".."

import { Link, ResponsiveImage, SvgIcon, TripModal, SpecialTag, WishlistButton } from ".."
import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"

export type TripTeaserNewProps = {
  external_id: number
  title: string
  description: string
  image: ResponsiveImageType
  duration: number
  price: string
  rawPrice: number
  uri: string
  bookDate?: string
  savings?: string
  isSpecial?: boolean
  addedValue?: boolean | null
  tripLocations?: string
  tripCode?: string
  primaryCountry?: string
  destinationSlug?: string
  specialOfferShortDescription?: string
  onRemoveFromWishList?: ({ contentType, nid }: { contentType: string; nid: number }) => void
}

const TripTeaserNew: FC<TripTeaserNewProps> = ({
  external_id,
  title,
  description,
  duration,
  price,
  rawPrice,
  savings,
  image,
  bookDate,
  uri,
  isSpecial,
  addedValue,
  tripLocations,
  tripCode,
  primaryCountry,
  destinationSlug,
  specialOfferShortDescription,
  onRemoveFromWishList,
}) => {
  const dispatch = useDispatch()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const wishlist = useSelector((state) => state.views.wishlist)
  const liked = wishlist?.trip?.some((trip: { nid: number }) => trip.nid === external_id)

  const addToWishList = useCallback(() => {
    dispatch(
      addNode({
        type: "trip",
        entity: { contentType: "trip", nid: external_id },
        idPropertyKey: "nid",
      })
    )
  }, [dispatch, external_id])

  const removeFromWishList = useCallback(() => {
    dispatch(removeNode({ type: "trip", findPredicate: { contentType: "trip", nid: external_id } }))
    onRemoveFromWishList?.({ contentType: "trip", nid: external_id })
  }, [dispatch, external_id, onRemoveFromWishList])

  return (
    <>
      <Stack
        position="relative"
        sx={(theme) => ({
          maxWidth: 408,
          height: "100%", // Take up full height
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 2,
          isolation: "isolate",
          transition: theme.transitions.create("box-shadow"),

          [theme.breakpoints.up("md")]: {
            "&:hover, &:focus-within": {
              "boxShadow": "0px 4px 16px 0px rgba(0, 0, 0, 0.08)",
              ".arrow-btn": { opacity: 1, right: 16 },
              ".request-quote-cta": { opacity: 1, visibility: "visible" },
            },
          },
        })}
      >
        <Link type="overlay" href={uri} aria-label={title} title={title} zIndex={"2!important"} />
        <Box overflow="hidden" position="relative">
          {isSpecial && (
            <SpecialTag
              bookDate={bookDate}
              savings={savings}
              addedValue={addedValue}
              specialOfferShortDescription={specialOfferShortDescription}
            />
          )}
          <ResponsiveImage
            image={image}
            pictureProps={{ position: "relative" }}
            sx={{
              display: "block",
              aspectRatio: "16/9",
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.standard * 1.5,
                }),
            }}
          />
          <ArrowHoverIcon href={uri} />
          <Box position="absolute" top={16} right={16} zIndex={3}>
            <WishlistButton liked={liked} onLike={addToWishList} onUnlike={removeFromWishList} />
          </Box>
        </Box>
        <ContentWrapper>
          <TeaserContent title={title} description={description} locations={tripLocations} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              marginTop: "auto", // This pushes the box to the bottom of the flex container
            }}
          >
            <TeaserFooter
              duration={duration}
              price={price}
              hasSavings={Boolean(savings)}
              rawPrice={rawPrice}
            />
            <RequestQuoteCta setIsModalOpen={setIsModalOpen} />
          </Box>
        </ContentWrapper>
      </Stack>
      <TripModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        trip={{
          id: tripCode || "",
          title,
          image,
          slug: uri,
          primaryCountry: primaryCountry || "",
          destinationSlug: destinationSlug || "",
        }}
      />
    </>
  )
}

export default TripTeaserNew

/** A sub-component for the content wrapper */
const ContentWrapper = ({ children }: PropsWithChildren) => {
  return (
    <Stack
      sx={(theme) => ({
        padding: { xs: 5, md: 6 },
        flexGrow: 1,
        borderRadius: `0 0 ${theme.spacing(2)} ${theme.spacing(2)}`,
        borderRight: `1px solid ${theme.palette["neutral.200"].main}`,
        borderBottom: `1px solid ${theme.palette["neutral.200"].main}`,
        borderLeft: `1px solid ${theme.palette["neutral.200"].main}`,
        bgcolor: theme.palette["neutral.100"].main,
        gap: 8,
      })}
    >
      {children}
    </Stack>
  )
}

/** A sub-component for the teaser content */
const TeaserContent: FC<{
  title: string
  description: string
  locations?: string
}> = ({ title, description, locations }) => {
  const truncateDescription = (text: string, maxLength: number = 130): string => {
    if (!text) return ""
    if (text.length <= maxLength) return text
    return `${text.slice(0, maxLength)}...`
  }

  const truncatedDescription = useMemo(() => truncateDescription(description), [description])
  return (
    <Stack gap={3}>
      <Typography
        variant="heading.md"
        component="h3"
        sx={{
          fontSize: "22px!important",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "140%",
          letterSpacing: "-0.22px",
        }}
      >
        {title}
      </Typography>
      {locations && (
        <Typography
          variant="body.md"
          sx={{
            "color": "#287E6F",
            "fontSize": "14px!important",
            "fontStyle": "normal",
            "fontWeight": 500,
            "lineHeight": "140%",
            "letterSpacing": "-0.112px",
            "& .location-separator": {
              color: "#64C5B2",
            },
          }}
          dangerouslySetInnerHTML={{ __html: locations }}
        />
      )}
      <Typography
        variant="body.md"
        color="neutral.600"
        component="div"
        dangerouslySetInnerHTML={{ __html: truncatedDescription }}
        sx={{
          "fontSize": "14px!important",
          "fontStyle": "normal",
          "fontWeight": 400,
          "lineHeight": "140%",
          "& p": {
            margin: 0,
          },
        }}
      />
    </Stack>
  )
}

/** A sub-component for the teaser footer */
const TeaserFooter: FC<{
  duration: number
  price: string
  hasSavings?: boolean
  rawPrice: number
}> = ({ duration, price, hasSavings, rawPrice }) => {
  const isPriceAvailable = rawPrice && rawPrice > 200
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Stack>
        {typeof duration === "number" && duration > 0 && (
          <Typography
            variant="body.md"
            sx={{
              fontSize: "14px!important",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "140%",
              textTransform: "uppercase",
            }}
          >
            {duration} days
          </Typography>
        )}
        {isPriceAvailable ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{ fontSize: "13px", fontStyle: "normal", fontWeight: 600, lineHeight: "140%" }}
            >
              From <span style={hasSavings ? { color: "#CE491C" } : {}}>{price}</span>
            </span>
            <span
              style={{
                fontSize: "10px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "140%",
                textTransform: "uppercase",
              }}
            >
              / per person
            </span>
          </Box>
        ) : (
          <Typography
            variant="body.md"
            sx={{
              fontSize: "14px!important",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "140%",
            }}
          >
            Please Inquire
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

/** A sub-component for the Request Quote CTA */
const RequestQuoteCta: FC<{ setIsModalOpen: (isOpen: boolean) => void }> = ({ setIsModalOpen }) => {
  // TODO: Add a link to the request quote page
  return (
    <Box
      className="request-quote-cta"
      position="relative"
      sx={{
        cursor: "pointer",
        alignSelf: "flex-end",
        pb: 1,
        opacity: {
          xs: 1,
          md: 0,
        },
        visibility: {
          xs: "visible",
          md: "hidden",
        },
        transition: (theme) =>
          theme.transitions.create(["opacity", "visibility"], {
            duration: theme.transitions.duration.standard * 1.5,
          }),
      }}
    >
      <Box
        role="button"
        onClick={() => setIsModalOpen(true)}
        sx={{
          display: "block",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
        }}
      />
      <Typography
        variant="body.md"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          fontSize: "13px!important",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "100%",
          letterSpacing: "-0.104px",
          color: (theme) => theme.palette["primary.900"].main,
        }}
      >
        Request a Quote
        <SvgIcon
          variant="arrowOutward"
          size={8}
          strokeMode
          sx={(theme) => ({
            fill: "none",
            stroke: theme.palette["primary.900"].main,
          })}
        />
      </Typography>
    </Box>
  )
}

/** A sub-component for the arrow hover icon */
const ArrowHoverIcon: FC<{ href: string }> = ({ href }) => {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      className="arrow-btn"
      sx={(theme) => ({
        "height": 52,
        "width": 52,
        "border": "1px solid transparent",
        "bgcolor": theme.palette["neutral.100"].main,
        "borderRadius": 9999,
        "lineHeight": 1,
        "transition": theme.transitions.create(["all", "background-color"], {
          duration: theme.transitions.duration.standard * 1.5,
        }),
        "opacity": {
          xs: 1,
          md: 0,
        },
        "position": "absolute",
        "bottom": 16,
        "zIndex": 4,
        "right": {
          xs: 16,
          md: 70,
        },
        "&:hover": {
          "bgcolor": theme.palette["primary.800"].main,
          "& svg": {
            fill: theme.palette["neutral.100"].main,
          },
        },
      })}
    >
      <Link
        type="overlay"
        href={href}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <SvgIcon
          variant="arrow"
          size={20}
          sx={(theme) => ({
            fill: theme.palette["neutral.900"].main,
            transform: "rotate(180deg)",
          })}
        />
      </Link>
    </Stack>
  )
}
